import Arthrosamid from "@/assets/logos/arthrosamid_logo.png";
import type { StatefulDiaryT } from "@/types/database/diaries";
import type { EntryViewT } from "@/types/database/entries";
import { UUIDs } from "@/types/uuids";
import React, { useEffect, useState } from "react";

import { StateCards } from "./components/StateCards";
import { TableChartCard } from "./components/TableChartCard";
import { TotalDiariesCard } from "./components/TotalDiariesCard";
import { WeeklyDataCard } from "./components/WeeklyDataCard";
import { EQ5DAnalyticsStat } from "./components/eq-5d/analytics";
import { Eq5dChart } from "./components/eq-5d/chart";
import { Eq5dTable } from "./components/eq-5d/table";
import { OxfordKneeAnalyticsStat } from "./components/oxford/analytics";
import { OxfordChart } from "./components/oxford/chart";
import { OxfordTable } from "./components/oxford/table";
import { VasAnalyticsStat } from "./components/vas/analytics";
import { VasChart } from "./components/vas/chart";
import { VasTable } from "./components/vas/table";
import { WomacAnalyticsStat } from "./components/womac/analytics";
import { WomacChart } from "./components/womac/chart";
import { WomacTable } from "./components/womac/table";
import type { PatientData } from "./types";
import {
	extractFormData,
	transformFormDataToChartData,
} from "./utils/chart-values";
import type { ChartDataItem } from "./utils/chart-values";

interface ArthrosamidAnalyticsProps {
	diaries: StatefulDiaryT[];
}

interface ChartData {
	pain: ChartDataItem[];
	womac: ChartDataItem[];
	eq5d: ChartDataItem[];
	oxford: ChartDataItem[];
}

export function ArthrosamidAnalytics({
	diaries = [],
}: ArthrosamidAnalyticsProps) {
	const [chartData, setChartData] = useState<ChartData>({
		pain: [],
		womac: [],
		eq5d: [],
		oxford: [],
	});

	useEffect(() => {
		if (!diaries?.length) {
			console.log("No diaries provided");
			return;
		}

		// Extract form data from diaries
		const extractedFormData = extractFormData(diaries);

		// Transform form data to chart data
		const transformedData = transformFormDataToChartData(extractedFormData);

		// Set chart data
		setChartData({
			pain: transformedData.painScore,
			womac: transformedData.womac,
			eq5d: transformedData.eq5d,
			oxford: transformedData.oxfordKnee,
		});
	}, [diaries]);

	// Get WOMAC entries for the chart
	const womacEntries = React.useMemo(() => {
		const entries: EntryViewT[] = [];
		for (const diary of diaries) {
			const womacTracker = diary.trackers_view?.find(
				(tracker) => tracker.form_id === UUIDs.forms.womac,
			);
			if (womacTracker?.entries_view) {
				entries.push(...womacTracker.entries_view);
			}
		}
		return entries;
	}, [diaries]);

	// Convert diaries to PatientData format for the table
	const patients: PatientData[] = React.useMemo(() => {
		console.log("Converting diaries to PatientData format");
		console.log("Number of diaries:", diaries.length);

		return diaries.map((diary) => {
			const womacTracker = diary.trackers_view?.find(
				(tracker) => tracker.form_id === UUIDs.forms.womac,
			);

			// Sort entries by date
			const sortedEntries =
				womacTracker?.entries_view?.sort((a, b) => {
					const timeA = a.calculated_entry_time
						? new Date(a.calculated_entry_time).getTime()
						: 0;
					const timeB = b.calculated_entry_time
						? new Date(b.calculated_entry_time).getTime()
						: 0;
					return timeA - timeB;
				}) || [];

			// Map entries to time points
			const womacEntries = sortedEntries.map((entry, index) => {
				let timePoint = "Baseline";
				if (index === 1) timePoint = "3 month";
				else if (index === 2) timePoint = "6 month";
				else if (index === 3) timePoint = "9 month";
				else if (index === 4) timePoint = "1 year";
				else if (index === 5) timePoint = "2 year";
				else if (index === 6) timePoint = "3 year";

				return {
					label: timePoint,
					pain: Number(
						entry.entry_values.find(
							(ev) => ev.input_id === UUIDs.womacInputs.womacPain,
						)?.value || 0,
					),
					function: Number(
						entry.entry_values.find(
							(ev) => ev.input_id === UUIDs.womacInputs.womacFunction,
						)?.value || 0,
					),
					stiffness: Number(
						entry.entry_values.find(
							(ev) => ev.input_id === UUIDs.womacInputs.womacStiffness,
						)?.value || 0,
					),
				};
			});

			// Get VAS entries
			const vasTracker = diary.trackers_view?.find(
				(tracker) => tracker.form_id === UUIDs.forms.womac,
			);
			const vasEntries =
				vasTracker?.entries_view?.map((entry, index) => {
					let timePoint = "Baseline";
					if (index === 1) timePoint = "3 month";
					else if (index === 2) timePoint = "6 month";
					else if (index === 3) timePoint = "9 month";
					else if (index === 4) timePoint = "1 year";
					else if (index === 5) timePoint = "2 year";
					else if (index === 6) timePoint = "3 year";

					return {
						label: timePoint,
						score: Number(
							entry.entry_values.find(
								(ev) => ev.input_id === UUIDs.painScores.painScore1,
							)?.value || 0,
						),
					};
				}) || [];

			console.log(`Patient ${diary.patient_id} VAS entries:`, vasEntries);

			return {
				patient_id: diary.patient_id,
				age: 0,
				medical_cartilage_change: "",
				lateral_cartilage_change: "",
				patellofemoral_cartilage_change: "",
				medial_cartilage_change: "",
				medical_meniscus_change: "",
				lateral_meniscus_change: "",
				patellofemoral_meniscus_change: "",
				medial_meniscus_change: "",
				effusion: "",
				trackers_view: diary.trackers_view || [],
				painScores: [],
				womac: womacEntries,
				oxford: [],
				vas: vasEntries,
				eq5d: [],
			};
		});
	}, [diaries]);

	return (
		<div className="space-y-6 mx-auto px-2 md:px-6 py-6">
			<div className="flex md:flex-row-reverse flex-col justify-start md:justify-between items-start md:items-center gap-2">
				<img src={Arthrosamid} alt="Arthrosamid Logo" className="h-12" />
				<h1 className="font-bold text-3xl">Analytics Dashboard</h1>
			</div>

			{/* Main Dashboard Content */}
			<div className="flex md:flex-row flex-col gap-6">
				<StateCards diaries={diaries} />
				<TotalDiariesCard diaries={diaries} />
				<WeeklyDataCard diaries={diaries} />
			</div>

			{/* Charts Section */}
			<div className="gap-6 grid md:grid-cols-2">
				{/* WOMAC Chart */}
				{chartData.womac.length > 0 && (
					<TableChartCard
						title="WOMAC Score"
						chart={<WomacChart chartData={chartData.womac} />}
						table={<WomacTable chartData={chartData.womac} />}
						analytics={<WomacAnalyticsStat diaries={diaries} />}
					/>
				)}

				{/* Pain Score Chart */}
				{chartData.pain.length > 0 && (
					<TableChartCard
						title="Pain Score"
						chart={<VasChart chartData={chartData.pain} />}
						table={<VasTable chartData={chartData.pain} />}
						analytics={<VasAnalyticsStat diaries={diaries} />}
					/>
				)}

				{/* EQ-5D Chart */}
				{chartData.eq5d.length > 0 && (
					<TableChartCard
						title="EQ-5D Score"
						chart={<Eq5dChart chartData={chartData.eq5d} />}
						table={<Eq5dTable chartData={chartData.eq5d} />}
						analytics={<EQ5DAnalyticsStat diaries={diaries} />}
					/>
				)}

				{/* Oxford Knee Chart */}
				{chartData.oxford.length > 0 && (
					<TableChartCard
						title="Oxford Knee Score"
						chart={<OxfordChart chartData={chartData.oxford} />}
						table={<OxfordTable chartData={chartData.oxford} />}
						analytics={<OxfordKneeAnalyticsStat diaries={diaries} />}
					/>
				)}
			</div>
		</div>
	);
}
